/**
 * ISO 3166-1 alpha-2 codes that CLDR still resolves to a display name but
 * which are deprecated or transitional and shadow a currently-assigned code
 * with the same English name (e.g. "UK" → GB, "FX" → FR, "SU" → RU). Excluding
 * them from the name→code map ensures lookups land on the canonical current
 * code.
 */
const DEPRECATED_ALPHA2 = new Set([
  'AN', // Netherlands Antilles → split into BQ/CW/SX
  'BU', // Burma → MM
  'CS', // Czechoslovakia / Serbia and Montenegro
  'DD', // East Germany → DE
  'FX', // Metropolitan France → FR
  'RH', // Southern Rhodesia → ZW
  'SF', // Finland (old) → FI
  'SU', // Soviet Union → RU and others
  'TP', // East Timor (old) → TL
  'UK', // United Kingdom alias → GB
  'VD', // North Vietnam → VN
  'YD', // South Yemen → YE
  'YU', // Yugoslavia
  'ZR', // Zaire → CD
]);

/**
 * Common English aliases CLDR does not surface as the primary region name
 * (e.g. "USA" for "United States", "Czech Republic" for "Czechia"). The
 * sweep below handles canonical CLDR names; this map fills the gaps.
 */
const NAME_ALIASES: Record<string, string> = {
  america: 'US',
  burma: 'MM',
  'czech republic': 'CZ',
  'democratic republic of the congo': 'CD',
  'great britain': 'GB',
  holland: 'NL',
  'republic of the congo': 'CG',
  uae: 'AE',
  uk: 'GB',
  usa: 'US',
  'united states of america': 'US',
};

let nameToCodeCache: Map<string, string> | null = null;

function buildNameToCode(): Map<string, string> {
  // `fallback: 'none'` makes `DisplayNames.of(code)` return `undefined` when
  // CLDR has no display name for that code — letting us distinguish real
  // regions from unassigned 2-letter slots.
  const display = new Intl.DisplayNames(['en'], {
    type: 'region',
    fallback: 'none',
  });
  const map = new Map<string, string>();

  for (let first = 65; first <= 90; first++) {
    for (let second = 65; second <= 90; second++) {
      const code = String.fromCharCode(first, second);
      if (DEPRECATED_ALPHA2.has(code)) {
        continue;
      }
      const name = display.of(code);
      if (!name || name === code) {
        continue;
      }

      const canonical = name.toLowerCase();
      map.set(canonical, code);

      // CLDR includes parenthetical and dash-suffixed disambiguators on a
      // handful of regions (e.g. "Myanmar (Burma)", "Congo - Kinshasa").
      // Also index the simpler form so callers that pass the short name
      // (which is what the backend typically stores) resolve correctly.
      const stripped = canonical
        .replace(/\s*\([^)]*\)\s*/g, '')
        .replace(/\s+-\s+.*$/, '')
        .trim();

      if (stripped && stripped !== canonical) {
        map.set(stripped, code);
      }
    }
  }

  for (const [alias, code] of Object.entries(NAME_ALIASES)) {
    map.set(alias, code);
  }

  return map;
}

function lookupByName(value: string): string | undefined {
  nameToCodeCache ??= buildNameToCode();
  return nameToCodeCache.get(value.toLowerCase());
}

/**
 * Normalize a raw country signal (Vercel header, cookie value, backend
 * display name, etc.) to a canonical 2-letter uppercase ISO 3166-1 alpha-2
 * code, or `undefined` when the input can't be resolved.
 *
 * Accepts either:
 *  - An already-valid ISO 3166-1 alpha-2 code (e.g. `"GB"`, `"us"`).
 *  - An English display name (e.g. `"United States"`, `"united kingdom"`,
 *    `"USA"`). Resolved via `Intl.DisplayNames` plus a small alias table for
 *    common variants CLDR doesn't surface as the primary name.
 *
 * Returns `undefined` for unknown inputs and for Vercel's `XX` unknown-geo
 * sentinel — preferred over a junk value reaching downstream consumers
 * (Stripe checkout, GrowthBook targeting) that treat anything truthy as a
 * real country (see DEV-5797).
 */
export function normalizeIsoCountry(
  value: string | null | undefined
): string | undefined {
  if (!value) {
    return;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return;
  }

  // Check the name/alias map first so deprecated 2-letter codes that double as
  // aliases ("UK" → GB) resolve to the canonical current code rather than
  // falling through the 2-letter fast path and being returned as-is.
  const aliased = lookupByName(trimmed);
  if (aliased) {
    return aliased;
  }

  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    const upper = trimmed.toUpperCase();
    // Vercel emits "XX" when it can't geolocate an IP. Without this filter,
    // those users land on a truthy-but-junk country attribute that fails the
    // US `$in` targeting list while passing the ROW `$nin` — mis-routing them
    // into USD-ROW.
    if (upper === 'XX') {
      return;
    }
    return upper;
  }

  return;
}
